/**
 * Audit feature — Zod schemas.
 *
 * Audit events are append-only log entries the backend emits for
 * every security-relevant action. They are the canonical
 * compliance record and are frequently exported, so field names
 * match the backend column names exactly — do not rename in
 * transit.
 */

import { z } from "zod";

// ── List filter (URL search params) ───────────────────────────────────────
//
// Every filter lives in the URL so every investigation link is
// deep-linkable and survives refresh. React state never mirrors
// these — the URL is the single source of truth. See
// /CLAUDE.md "URL is the source of truth for list/filter state".

export const auditListSearchSchema = z.object({
  /** Tenant audit-page tab. Platform audit ignores this field. */
  view: z.enum(["events", "windows-authentication", "evidence", "signed-records"]).optional(),
  /** Full-text search across actor email, event name, target id. */
  q: z.string().optional(),
  /** Event name filter, e.g. `user.passkey.created`. Exact match. */
  event: z.string().optional(),
  /**
   * Left-anchored event-name prefix, e.g. `frontline.`. Backs the
   * one-click compliance-evidence timeline: every event in a
   * ceremony family (pair, classify, badge, supervised approval,
   * break-glass) in one attributable, deep-linkable view. The
   * backend matches `action LIKE 'prefix%'` (metacharacters
   * escaped); AND-composes with every other filter.
   */
  event_prefix: z.string().optional(),
  /** Actor email or id fragment. */
  actor: z.string().optional(),
  /**
   * Exact actor UUID — the "all actions BY this identity" pivot.
   * For a service account this is the SA's admin_users.id. The
   * backend expects a UUID and 400s on a malformed value, so
   * deep-links from feature pages should pass a well-formed id.
   */
  actor_id: z.string().uuid().optional(),
  /**
   * Exact resource UUID — the "all actions ON this identity"
   * pivot. For a service account this is the SA's
   * admin_users.id too (target_id on lifecycle events).
   */
  resource_id: z.string().uuid().optional(),
  /**
   * Exact subject UUID — the per-ENTITY pivot (§5d): "everything that
   * ever happened to/by this entity" across its whole lifecycle,
   * regardless of resource_type. For an agent this returns the COMPLETE
   * trail — registry lifecycle, delegations, grants, AND T3 approval
   * decisions — in one query, because every agent emit site stamps the
   * agent as the subject. Distinct from resource_id, which pins the
   * granular resource a single action touched (a grant jti, an approval
   * id). The agent Activity tab pins this.
   */
  subject_id: z.string().uuid().optional(),
  /** Subject-type companion narrow (e.g. "agent"). AND-composes with subject_id. */
  subject_type: z.string().optional(),
  /** Resource type filter (e.g. "credential", "user"). */
  resource_type: z.string().optional(),
  /** Exact IP address (incident response pivot). */
  ip: z.string().optional(),
  /** Exact bearer-session UUID — the SOC "all actions from this session" pivot. */
  session: z.string().optional(),
  /** ISO-8601 start of range (inclusive). */
  from: z.string().optional(),
  /** ISO-8601 end of range (inclusive). */
  to: z.string().optional(),
  /** Result filter. */
  result: z.enum(["success", "failure", "denied"]).optional(),
  /**
   * Windows authentication view only: which population to show. `witnessed` is a sign-in PureID
   * completed itself; `reported` is one an enrolled machine told us about. Absent = both.
   */
  origin: z.enum(["witnessed", "reported"]).optional(),
  /**
   * Windows authentication view only: successes or failures. Deliberately NOT the `result` field
   * above — that one carries `denied` and belongs to the general event list, and a tab switch must
   * not silently reinterpret one filter as the other.
   */
  outcome: z.enum(["success", "failure"]).optional(),
  /**
   * Canonical time-range preset. When set, the view renders as
   * the rolling window (e.g. 24h = now-24h to now). Mutually
   * exclusive with from/to: setting a preset clears the explicit
   * range so the query is unambiguous.
   */
  preset: z.enum(["15m", "1h", "24h", "7d"]).optional(),
  /** Offset-based pagination. */
  offset: z.coerce.number().int().nonnegative().optional(),
  /**
   * Page size. Bounded to the backend's MaxListLimit (200); out-of-range
   * values are clamped rather than rejected so a hand-edited URL still
   * resolves to a valid page. Omitted → backend default (50).
   */
  limit: z.coerce.number().int().min(1).max(200).catch(50).optional(),
});
export type AuditListSearch = z.infer<typeof auditListSearchSchema>;

// ── Row ───────────────────────────────────────────────────────────────────

export const auditEventSchema = z.object({
  id: z.string(),
  // Tenant-scoped events carry tenant_id; platform-scoped
  // events carry target_tenant_id instead. Both are optional
  // because the backend uses different response shapes per scope.
  tenant_id: z.string().nullable().optional(),
  target_tenant_id: z.string().nullable().optional(),
  target_tenant_name: z.string().nullable().optional(),
  actor_id: z.string().nullable(),
  actor_email: z.string().nullable(),
  // actor_name — the actor's display name; for service accounts (synthetic emails) it's
  // the only human-recognisable label. Optional for back-compat with cached responses.
  actor_name: z.string().nullable().optional(),
  actor_role: z.string().nullable(),
  event: z.string(),
  result: z.enum(["success", "failure", "denied"]),
  target_type: z.string().nullable(),
  target_id: z.string().nullable(),
  /**
   * Human-readable target label (credential friendly name, user
   * email, app name …). The backend auto-resolves this from the
   * target_id when the emit site didn't stamp it; the UI prefers
   * it over the UUID for display. Null only when the resource
   * type has no resolver registered or the lookup was not-found.
   */
  resource_name: z.string().nullable().optional(),
  /**
   * Backend-rendered narrative ("Alice approved Bob's access to
   * Microag EdgeDeployment"). Computed server-side by
   * audit.RenderNarrative from the row's structured fields, so
   * exports (CSV / NDJSON / iOS / CLI) read the same sentence the
   * SPA shows. Always non-empty — falls back to a "Domain · verb"
   * label when no template is registered for the action. The
   * audit table prefers this over the legacy frontend template
   * engine (`auditEventDescription` from @pureid/ui) so the source
   * of truth is the backend, per backend/internal/ops/audit/README.md clause N6.
   */
  description: z.string().optional(),
  ip_address: z.string().nullable(),
  user_agent: z.string().nullable(),
  request_id: z.string().nullable(),
  /**
   * Bearer-token session that produced the action. Nullable for
   * pre-auth flows. SOC investigations pivot "all actions from
   * this session" off this column.
   */
  session_id: z.string().nullable().optional(),
  device_id: z.string().nullable().optional(),
  /** Classified device: desktop_browser | mobile_browser | security_key | unknown. */
  device_type: z.string().nullable().optional(),
  /** ISO-3166-1 alpha-2 country code (e.g. "US"). */
  geo_country: z.string().nullable().optional(),
  organization_id: z.string().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).default({}),
  created_at: z.string(),
});
export type AuditEvent = z.infer<typeof auditEventSchema>;

export const auditListResponseSchema = z.object({
  data: z.array(auditEventSchema),
  pagination: z.object({
    total: z.number().int().nonnegative(),
    offset: z.number().int().nonnegative(),
    limit: z.number().int().nonnegative(),
  }),
});
export type AuditListResponse = z.infer<typeof auditListResponseSchema>;

/**
 * One row of the Windows authentication view.
 *
 * TWO TRUST LEVELS SHARE THIS LIST AND `origin` IS WHAT KEEPS THEM APART. A `witnessed` row is a
 * sign-in PureID completed itself and issued a certificate for. A `reported` row is what an
 * enrolled machine, authenticated by its own device key, told us happened on it — a sign-in that
 * failed locally, or one that succeeded offline against a certificate the machine already held.
 * Nothing on the server saw the second kind.
 *
 * `origin` and `outcome` are REQUIRED, never `.optional()`: a row whose origin the UI had to guess
 * at would be a claim rendered as an observation, which is precisely the misreading this view was
 * extended to stop. Everything a reported row alone carries — the reason, the claimed identifier,
 * the machine's correlation id and its own clock — is optional, and so is the user, because a
 * reported identifier that resolves to nobody is evidence worth keeping rather than a parse error.
 */
export const windowsAuthenticationSchema = z.object({
  id: z.string(),
  origin: z.enum(["witnessed", "reported"]),
  outcome: z.enum(["success", "failure"]),
  failure_reason: z.string().optional(),
  tenant_id: z.string(),
  user_id: z.string().optional(),
  user_display_name: z.string().optional(),
  user_primary_email: z.string().optional(),
  /** What the person typed at the tile, as claimed by the machine. Reported rows only. */
  attempted_identifier: z.string().optional(),
  workstation_id: z.string(),
  hostname_reported: z.string().optional(),
  jkt: z.string(),
  workstation_status: z.string(),
  certificate_serial: z.string().optional(),
  correlation_id: z.string().optional(),
  /** The machine's own clock, kept beside observed_at so the skew stays visible. */
  reported_occurred_at: z.string().optional(),
  /** The server's clock on every row, and the field the list is ordered by. */
  observed_at: z.string(),
});
export type WindowsAuthentication = z.infer<typeof windowsAuthenticationSchema>;

export const windowsAuthenticationsResponseSchema = z.object({
  data: z.array(windowsAuthenticationSchema),
  pagination: z.object({
    total: z.number().int().nonnegative(),
    offset: z.number().int().nonnegative(),
    limit: z.number().int().nonnegative(),
  }),
});
export type WindowsAuthenticationsResponse = z.infer<typeof windowsAuthenticationsResponseSchema>;
